import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Lock,
  RefreshCw,
  Trash2,
  Mail,
  Phone,
  Building2,
  Plane,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowLeft,
} from "lucide-react";
import { COMPANY } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nps_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rfqs, setRfqs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  const fetchData = useCallback(async (t) => {
    setLoading(true);
    try {
      const [rfqsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/rfqs`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Token": t } }),
      ]);
      setRfqs(rfqsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Failed to load RFQs.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchData(token);
  }, [token, fetchData]);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword("");
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRfqs([]);
    setStats(null);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/rfqs/${id}`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Marked as ${status}.`);
      fetchData(token);
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteRfq = async (id) => {
    if (!window.confirm("Delete this RFQ permanently?")) return;
    try {
      await axios.delete(`${API}/admin/rfqs/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("RFQ deleted.");
      setSelectedId(null);
      fetchData(token);
    } catch {
      toast.error("Delete failed.");
    }
  };

  // ====== LOGIN VIEW ======
  if (!token) {
    return (
      <div
        data-testid="admin-login-page"
        className="min-h-screen bg-[#0B1D3A] flex items-center justify-center px-5"
      >
        <div className="w-full max-w-md">
          <Link
            to="/"
            data-testid="admin-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#C9A961] text-sm mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>

          <div className="bg-white rounded-sm p-8 border-t-2 border-[#C9A961]">
            <div className="flex flex-col items-center mb-7">
              <img
                src={COMPANY.logo}
                alt="NPS"
                className="h-28 w-auto object-contain mb-2"
              />
              <div>
                <p className="font-display font-extrabold text-[#0B1D3A] text-[15px]">
                  NOBLE POWER SOLUTIONS
                </p>
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#C9A961]">
                  Admin Console
                </p>
              </div>
            </div>

            <h1 className="font-display font-bold text-[#0B1D3A] text-2xl">
              Sign in to continue
            </h1>
            <p className="text-[#4B5563] text-sm mt-2">
              Restricted area. Enter admin password to view RFQ submissions.
            </p>

            <form onSubmit={login} className="mt-7 space-y-5">
              <div>
                <label
                  htmlFor="admin-password"
                  className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4B5563] mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    id="admin-password"
                    data-testid="admin-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white border border-[#E5E7EB] rounded-sm pl-10 pr-4 py-3 text-[#111827] focus:outline-none focus:border-[#0B1D3A] focus:ring-1 focus:ring-[#0B1D3A]"
                    placeholder="Enter admin password"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="admin-login-button"
                disabled={loading}
                className="w-full bg-[#0B1D3A] hover:bg-[#1E3A5F] disabled:opacity-60 text-white px-6 py-3.5 font-semibold tracking-wide rounded-sm transition-colors"
              >
                {loading ? "Authenticating…" : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ====== DASHBOARD VIEW ======
  const filtered =
    filter === "all" ? rfqs : rfqs.filter((r) => r.status === filter || r.urgency === filter);
  const selected = rfqs.find((r) => r.id === selectedId);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-[#0B1D3A] text-white border-b-2 border-[#C9A961]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="admin-logo-link">
            <img
              src={COMPANY.logo}
              alt="NPS"
              className="h-14 w-auto object-contain"
              style={{
                filter:
                  "drop-shadow(0 0 10px rgba(255,255,255,0.3)) drop-shadow(0 0 24px rgba(201,169,97,0.3))",
              }}
            />
            <div className="ml-4 leading-tight border-l border-white/15 pl-4">
              <p className="font-display font-extrabold text-white text-[13px] tracking-tight">
                NOBLE POWER SOLUTIONS
              </p>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#C9A961]">
                Admin · RFQ Inbox
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={() => fetchData(token)}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#C9A961] text-white/85 px-3 py-2 text-sm rounded-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-[#D32F2F] hover:bg-[#B91C1C] text-white px-3 py-2 text-sm rounded-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <StatTile label="Total" value={stats.total} testid="stat-total" />
            <StatTile label="New" value={stats.new} accent="navy" testid="stat-new" />
            <StatTile label="Reviewed" value={stats.reviewed} accent="gold" testid="stat-reviewed" />
            <StatTile label="Closed" value={stats.closed} accent="muted" testid="stat-closed" />
            <StatTile label="AOG" value={stats.aog} accent="red" testid="stat-aog" />
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            { id: "all", label: "All" },
            { id: "new", label: "New" },
            { id: "reviewed", label: "Reviewed" },
            { id: "closed", label: "Closed" },
            { id: "aog", label: "AOG" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`filter-${t.id}`}
              onClick={() => setFilter(t.id)}
              className={`px-4 py-2 text-sm font-semibold rounded-sm border transition-colors ${
                filter === t.id
                  ? "bg-[#0B1D3A] border-[#0B1D3A] text-white"
                  : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#1E3A5F]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-[#E5E7EB] rounded-sm divide-y divide-[#F1F2F4] max-h-[70vh] overflow-y-auto">
              {filtered.length === 0 && (
                <div className="p-10 text-center text-[#4B5563] text-sm">
                  {loading ? "Loading…" : "No RFQs to display."}
                </div>
              )}
              {filtered.map((r) => (
                <button
                  key={r.id}
                  data-testid={`rfq-item-${r.id}`}
                  onClick={() => setSelectedId(r.id)}
                  className={`w-full text-left p-4 hover:bg-[#F8F9FA] transition-colors ${
                    selectedId === r.id ? "bg-[#F8F9FA] border-l-[3px] border-[#C9A961]" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-[#0B1D3A] text-sm truncate">
                        {r.company}
                      </p>
                      <p className="text-[12px] text-[#4B5563] truncate">{r.name} · {r.email}</p>
                    </div>
                    <UrgencyBadge urgency={r.urgency} />
                  </div>
                  <p className="mt-2 text-[12px] text-[#4B5563] line-clamp-2">
                    {r.message}
                  </p>
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-[#9CA3AF] font-mono">
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                    <span>·</span>
                    <StatusPill status={r.status} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {!selected ? (
              <div className="bg-white border border-[#E5E7EB] rounded-sm p-12 text-center text-[#4B5563]">
                <Mail className="w-8 h-8 mx-auto text-[#9CA3AF]" />
                <p className="mt-3 text-sm">Select an RFQ to view full details.</p>
              </div>
            ) : (
              <article
                data-testid="rfq-detail"
                className="bg-white border border-[#E5E7EB] rounded-sm p-7"
              >
                <div className="flex items-start justify-between gap-4 pb-5 border-b border-[#F1F2F4]">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <UrgencyBadge urgency={selected.urgency} />
                      <StatusPill status={selected.status} />
                    </div>
                    <h2 className="font-display font-bold text-[#0B1D3A] text-xl">
                      {selected.company}
                    </h2>
                    <p className="text-sm text-[#4B5563] mt-1">
                      {selected.name} · {new Date(selected.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    data-testid="rfq-delete-button"
                    onClick={() => deleteRfq(selected.id)}
                    className="inline-flex items-center gap-2 border border-[#FECACA] hover:bg-[#FEF2F2] text-[#B91C1C] px-3 py-1.5 text-sm rounded-sm transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>

                <dl className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
                  <DetailRow icon={Mail} label="Email" value={selected.email} href={`mailto:${selected.email}`} />
                  <DetailRow icon={Phone} label="Phone" value={selected.phone || "—"} href={selected.phone ? `tel:${selected.phone}` : null} />
                  <DetailRow icon={Plane} label="Aircraft" value={selected.aircraft_type || "—"} />
                  <DetailRow icon={Building2} label="Part #" value={selected.part_number || "—"} />
                  <DetailRow icon={CheckCircle2} label="Service" value={selected.service_type || "—"} />
                </dl>

                <div className="mt-6">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4B5563] mb-2">
                    Message
                  </p>
                  <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-sm p-4 text-sm text-[#111827] whitespace-pre-wrap leading-relaxed">
                    {selected.message}
                  </div>
                </div>

                <div className="mt-7 pt-5 border-t border-[#F1F2F4] flex flex-wrap gap-2">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4B5563] w-full mb-1">
                    Update Status
                  </p>
                  {["new", "reviewed", "closed"].map((s) => (
                    <button
                      key={s}
                      data-testid={`status-button-${s}`}
                      onClick={() => updateStatus(selected.id, s)}
                      disabled={selected.status === s}
                      className={`px-4 py-2 text-sm font-semibold rounded-sm border transition-colors ${
                        selected.status === s
                          ? "bg-[#0B1D3A] border-[#0B1D3A] text-white"
                          : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#1E3A5F]"
                      }`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </article>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatTile({ label, value, accent = "default", testid }) {
  const accentMap = {
    default: "border-[#E5E7EB]",
    navy: "border-[#0B1D3A]",
    gold: "border-[#C9A961]",
    red: "border-[#D32F2F]",
    muted: "border-[#9CA3AF]",
  };
  return (
    <div
      data-testid={testid}
      className={`bg-white border-t-2 ${accentMap[accent]} border-l border-r border-b border-[#E5E7EB] rounded-sm p-4`}
    >
      <p className="font-display font-extrabold text-[#0B1D3A] text-2xl">{value}</p>
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] mt-1">{label}</p>
    </div>
  );
}

function UrgencyBadge({ urgency }) {
  if (urgency === "aog") {
    return (
      <span className="inline-flex items-center gap-1 bg-[#D32F2F] text-white text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm">
        <AlertTriangle className="w-3 h-3" /> AOG
      </span>
    );
  }
  if (urgency === "urgent") {
    return (
      <span className="inline-flex items-center gap-1 bg-[#C9A961] text-[#0B1D3A] text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm">
        Urgent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-[#F8F9FA] border border-[#E5E7EB] text-[#4B5563] text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-sm">
      Standard
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    new: { text: "New", cls: "text-[#0B1D3A]", icon: Clock },
    reviewed: { text: "Reviewed", cls: "text-[#C9A961]", icon: CheckCircle2 },
    closed: { text: "Closed", cls: "text-[#9CA3AF]", icon: CheckCircle2 },
  };
  const s = map[status] || map.new;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.text}
    </span>
  );
}

function DetailRow({ icon: Icon, label, value, href }) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4B5563] mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </p>
      {href ? (
        <a href={href} className="text-[#0B1D3A] font-semibold hover:text-[#C9A961] break-all">
          {value}
        </a>
      ) : (
        <p className="text-[#111827] font-medium break-all">{value}</p>
      )}
    </div>
  );
}
